import React, { useState, useContext, useEffect } from "react";
import { Search, Bot, Bookmark, BookmarkCheck } from "lucide-react";
import { auth } from '../firebase.config';
import BotResponse from "./BotResponse";
import { AuthContext } from "../context/AuthContext";
import { AgentService } from "../services/AgentService";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

// Get API key from environment variable or use fallback for development
const AGENT_AI_API_KEY = process.env.REACT_APP_AGENT_AI_API_KEY;

// Common queries to suggest to users
const COMMON_QUERIES = [
  "Sales",
  "HR",
  "Marketing",
  "Operations",
  "Support"
];

// Weight lifting emojis for the running animation
const LIFTING_EMOJIS = ["🏋️", "🏋️‍♂️", "🏋️‍♀️"];

// Earth emojis for the searching animation
const createEarthString = (count) => {
  return "🌎".repeat(count);
};

// Add this helper function at the top of your file (outside the component)
const convertJsonToMarkdown = (json, level = 0) => {
  // If it's a string, just return it
  if (typeof json === 'string') return json;

  // If it's null or undefined
  if (json === null) return 'null';
  if (json === undefined) return 'undefined';

  // If it's a primitive (number, boolean)
  if (typeof json !== 'object') return String(json);

  // Check if it's an empty object or array
  if (Object.keys(json).length === 0) {
    return Array.isArray(json) ? '[]' : '{}';
  }

  let markdown = '';
  const indent = '  '.repeat(level);

  // Handle arrays
  if (Array.isArray(json)) {
    // Special case for simple arrays (of primitives)
    const isSimpleArray = json.every(item => typeof item !== 'object' || item === null);

    if (isSimpleArray) {
      json.forEach((item, index) => {
        markdown += `${level > 0 ? indent : ''}${index + 1}. ${item}\n`;
      });
      return markdown;
    }

    // For complex arrays
    json.forEach((item, index) => {
      if (typeof item === 'object' && item !== null) {
        // If item has a name or title property, use it as a header
        const title = item.name || item.title || item.id || `Item ${index + 1}`;
        markdown += `${level > 0 ? indent : ''}### ${title}\n\n`;

        // Convert the rest of the object
        markdown += convertJsonToMarkdown(item, level + 1);
        markdown += '\n';
      } else {
        markdown += `${level > 0 ? indent : ''}${index + 1}. ${item}\n`;
      }
    });

    return markdown;
  }

  // Handle error responses specially
  if (json.error || json.detail) {
    const errorMsg = json.error || json.detail;
    return `### Error\n\n${errorMsg}`;
  }

  // Handle objects
  const keys = Object.keys(json);

  // Check if it's an error response with nested error details
  if (json.response && (json.response.error || json.response.detail)) {
    const errorMsg = json.response.error || json.response.detail;
    return `### Error\n\n${errorMsg}`;
  }

  // Process simple objects with key-value pairs
  keys.forEach(key => {
    const value = json[key];

    // Handle nested objects and arrays
    if (typeof value === 'object' && value !== null) {
      // Skip empty objects/arrays
      if (Object.keys(value).length === 0) {
        markdown += `${level > 0 ? indent : ''}**${key}**: ${Array.isArray(value) ? '[]' : '{}'}\n\n`;
      }
      // Special case for arrays with simple values
      else if (Array.isArray(value) && value.every(item => typeof item !== 'object' || item === null)) {
        markdown += `${level > 0 ? indent : ''}**${key}**:\n`;
        value.forEach((item, index) => {
          markdown += `${indent}  ${index + 1}. ${item}\n`;
        });
        markdown += '\n';
      }
      // For tables or records that look like they should be tables
      else if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object'
        && value.every(item => typeof item === 'object')
        && value.every(item => Object.keys(item).every(k => typeof item[k] !== 'object' || item[k] === null))) {
        // Create a markdown table
        const headers = Array.from(new Set(value.flatMap(item => Object.keys(item))));

        // Start table
        markdown += `${level > 0 ? indent : ''}**${key}**:\n\n|`;

        // Add headers (limit to first 5 for readability)
        const limitedHeaders = headers.slice(0, 5);
        limitedHeaders.forEach(header => {
          markdown += ` ${header} |`;
        });
        markdown += '\n|';

        // Add separator
        limitedHeaders.forEach(() => {
          markdown += ' --- |';
        });
        markdown += '\n';

        // Add rows (limit to first 10 rows for readability)
        const limitedRows = value.slice(0, 10);
        limitedRows.forEach(row => {
          markdown += '|';
          limitedHeaders.forEach(header => {
            let cellValue = row[header] === undefined ? '' : row[header];
            if (cellValue === null) cellValue = 'null';
            if (typeof cellValue === 'object') cellValue = JSON.stringify(cellValue);
            markdown += ` ${cellValue} |`;
          });
          markdown += '\n';
        });

        // Add a note if data was truncated
        if (value.length > 10 || headers.length > 5) {
          markdown += `\n*Table showing ${limitedRows.length} of ${value.length} rows`;
          if (headers.length > 5) markdown += ` and ${limitedHeaders.length} of ${headers.length} columns`;
          markdown += '*\n\n';
        } else {
          markdown += '\n';
        }
      }
      // For deeply nested objects, create nested lists
      else {
        markdown += `${level > 0 ? indent : ''}**${key}**:\n`;
        markdown += convertJsonToMarkdown(value, level + 1);
        markdown += '\n';
      }
    }
    // Handle simple key-value pairs
    else {
      markdown += `${level > 0 ? indent : ''}**${key}**: ${value}\n\n`;
    }
  });

  return markdown;
};

const SearchAgentsDialog = ({ isOpen, onClose, initialAgent = null, onAgentSaved, onAgentRemoved }) => {
  const { currentUser } = useContext(AuthContext);
  const [query, setQuery] = useState("");
  const [intent, setIntent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(initialAgent);
  const [agentInputs, setAgentInputs] = useState({});
  const [agentResponse, setAgentResponse] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [savedAgentIds, setSavedAgentIds] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [agentFromSearch, setAgentFromSearch] = useState(false);

  // Animation states
  const [earthCount, setEarthCount] = useState(1);
  const [liftingIndex, setLiftingIndex] = useState(0);

  // Earth emoji animation
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setEarthCount(prev => prev === 3 ? 1 : prev + 1);
      }, 500);

      return () => clearInterval(interval);
    }
  }, [isLoading]);

  // Lifting emoji animation
  useEffect(() => {
    if (isRunning) {
      const interval = setInterval(() => {
        setLiftingIndex(prev => (prev + 1) % LIFTING_EMOJIS.length);
      }, 600);

      return () => clearInterval(interval);
    }
  }, [isRunning]);

  const handleSearch = async (e) => {
    e.preventDefault();

    // Ensure at least one of query or intent is provided
    if (!query && !intent) {
      return;
    }

    setIsLoading(true);
    let retryCount = 0;
    const maxRetries = 2;

    const performSearch = async () => {
      try {
        // First, load saved agents to check which ones are already saved
        if (currentUser?.uid) {
          const savedAgents = await AgentService.getUserAgents(currentUser.uid);
          setSavedAgentIds(savedAgents.map(agent => agent.agentId));
        }

        const response = await fetch("https://api-lr.agent.ai/v1/action/find_agents", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${AGENT_AI_API_KEY}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: "public",
            query: query,
            intent: intent
          }),
        });

        // Check if the HTTP status is 501
        if (response.status === 501) {
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = 1000 * Math.pow(2, retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
            return performSearch();
          }
        }

        // Try to parse the response
        const data = await response.json();

        // Also check if the response contains a status: 501
        if (data && data.status === 501) {
          if (retryCount < maxRetries) {
            retryCount++;
            const delay = 1000 * Math.pow(2, retryCount);
            await new Promise(resolve => setTimeout(resolve, delay));
            return performSearch();
          }
          throw new Error(`API request failed with status 501`);
        }

        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }

        setAgents(data.response || []);
        setShowSearchResults(true);

        return true;
      } catch (error) {
        // Check for 501 in the error message from either source
        if ((error.message.includes("501") ||
          (error.response && error.response.status === 501) ||
          (error.data && error.data.status === 501)) &&
          retryCount < maxRetries) {
          retryCount++;
          const delay = 1000 * Math.pow(2, retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return performSearch();
        }

        console.error("Error searching for agents:", error);
        return false;
      }
    };

    try {
      await performSearch();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion);
  };

  const handleAgentSelect = (agent, fromSearch = true) => {
    setSelectedAgent(agent);
    setIsSaved(savedAgentIds.includes(agent.agent_id));
    setAgentFromSearch(fromSearch);

    // Initialize input fields based on the agent's parameters
    if (agent.invoke_agent_input?.function?.parameters?.properties) {
      const initialInputs = {};
      const properties = agent.invoke_agent_input.function.parameters.properties;

      Object.keys(properties).forEach(key => {
        initialInputs[key] = "";
      });

      setAgentInputs(initialInputs);
    }
  };

  const handleInputChange = (paramName, value) => {
    setAgentInputs(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const handleRunAgent = async () => {
    setIsRunning(true);
    setAgentResponse("");

    try {
      // Create a properly formatted input string with the exact spacing shown in the sample
      let inputString = "{ ";

      // Add each property with proper spacing
      const entries = Object.entries(agentInputs);
      entries.forEach(([key, value], index) => {
        inputString += `\"${key}\" : \"${value}\"`;
        if (index < entries.length - 1) {
          inputString += ", ";
        }
      });

      inputString += " }";

      // Get agent ID from either property
      const agentId = selectedAgent?.agent_id || selectedAgent?.agentId;

      // Make sure we have the agent ID
      if (!agentId) {
        throw new Error("Agent ID is missing");
      }

      const requestBody = {
        id: agentId,
        input: inputString
      };

      const response = await fetch("https://api-lr.agent.ai/v1/action/invoke_agent", {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${AGENT_AI_API_KEY}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      // Format the response based on its type
      let formattedResponse;

      if (typeof data.response === 'string') {
        // If it's already a string, use it directly
        formattedResponse = data.response;
      } else if (data.response && typeof data.response === 'object') {
        // Convert the JSON to markdown UI elements
        formattedResponse = convertJsonToMarkdown(data.response);
      } else {
        // Handle other cases
        formattedResponse = "No response received or invalid response format.";
      }

      setAgentResponse(formattedResponse);
    } catch (error) {
      console.error("Error running agent:", error);
      setAgentResponse("### Error\n\nFailed to run agent. Please try again.");
    } finally {
      setIsRunning(false);
    }
  };

  const handleSaveAgent = async (e, agent = null) => {
    e?.stopPropagation(); // Prevent triggering the parent div click

    const agentToSave = agent || selectedAgent;
    if (!currentUser?.uid || !agentToSave) return;

    setIsSaving(true);
    try {
      const savedAgent = await AgentService.saveAgent(currentUser.uid, agentToSave);
      setIsSaved(true);
      setSavedAgentIds(prev => [...prev, agentToSave.agent_id]);
      if (onAgentSaved) {
        onAgentSaved(savedAgent);
      }
    } catch (error) {
      console.error("Error saving agent:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveAgent = async () => {
    if (!currentUser?.uid || !selectedAgent?.id) return;

    setIsSaving(true);
    try {
      await AgentService.removeAgent(selectedAgent.id);
      setIsSaved(false);
      setSavedAgentIds(prev => prev.filter(id => id !== selectedAgent.agent_id));
      if (onAgentRemoved) {
        onAgentRemoved(selectedAgent.id);
      }
    } catch (error) {
      console.error("Error removing agent:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    // Don't clear the selected agent, just the response
    setAgentResponse("");
  };

  const handleBackToSearchResults = () => {
    setSelectedAgent(null);
    setShowSearchResults(true);
  }

  const handleBackToSearch = () => {
    setShowSearchResults(false);
  }

  // Initialize from initialAgent if provided
  React.useEffect(() => {
    if (initialAgent) {
      // Convert the format if this is a saved agent
      const formattedAgent = {
        ...initialAgent,
        // Ensure agent_id is available regardless of source
        agent_id: initialAgent.agent_id || initialAgent.agentId
      };

      setSelectedAgent(formattedAgent);
      setAgentFromSearch(false);

      if (formattedAgent.invoke_agent_input?.function?.parameters?.properties) {
        const initialInputs = {};
        const properties = formattedAgent.invoke_agent_input.function.parameters.properties;

        Object.keys(properties).forEach(key => {
          initialInputs[key] = "";
        });

        setAgentInputs(initialInputs);
      }

      // If it came from saved agents, mark it as saved
      if (initialAgent.id) {
        setIsSaved(true);
      }
    }

    // Load saved agent IDs when component mounts
    const loadSavedAgentIds = async () => {
      if (currentUser?.uid) {
        const savedAgents = await AgentService.getUserAgents(currentUser.uid);
        setSavedAgentIds(savedAgents.map(agent => agent.agentId));
      }
    };

    loadSavedAgentIds();
  }, [initialAgent, currentUser?.uid]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] max-h-[80vh] overflow-auto" style={{ marginTop: "5vh" }}>
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold tracking-tight flex items-center justify-between">
            <span>
              {selectedAgent
                ? selectedAgent.name
                : showSearchResults
                  ? "Search Results"
                  : "Search AI Agents"
              }
            </span>
            {selectedAgent && !agentResponse && (
              <button
                onClick={isSaved ? handleRemoveAgent : handleSaveAgent}
                disabled={isSaving}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium ${isSaved
                    ? "bg-green-100 text-green-700 hover:bg-green-200"
                    : "bg-primary/10 text-primary hover:bg-primary/20"
                  }`}
              >
                {isSaved ? (
                  <>
                    <BookmarkCheck className="w-4 h-4" />
                    <span>Saved</span>
                  </>
                ) : (
                  <>
                    <Bookmark className="w-4 h-4" />
                    <span>{isSaving ? "Saving..." : "Save Agent"}</span>
                  </>
                )}
              </button>
            )}
          </DialogTitle>
        </DialogHeader>

        {selectedAgent ? (
          agentResponse ? (
            <div className="mt-4">
              <button
                onClick={handleBack}
                className="mb-4 text-sm px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
              >
                ← Back to agent
              </button>
              <div className="p-4 bg-gray-50 rounded-md">
                <BotResponse
                  response={agentResponse}
                  isLoading={isRunning}
                  isSpeakerDisabled={false}
                />
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <button
                onClick={agentFromSearch ? handleBackToSearchResults : onClose}
                className="mb-4 text-sm px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
              >
                {agentFromSearch ? "← Back to search results" : "← Back to all agents"}
              </button>

              <div className="flex items-center mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                  <img
                    src={selectedAgent.icon?.startsWith("/") ? `https://agent.ai${selectedAgent.icon}` : selectedAgent.icon || "/icons/ai-agent.svg"}
                    alt={selectedAgent.name}
                    className="w-6 h-6"
                  />
                </div>
                <div>
                  <h3 className="font-medium">{selectedAgent.name}</h3>
                  <p className="text-sm text-gray-500">{selectedAgent.description}</p>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {selectedAgent.invoke_agent_input?.function?.parameters?.properties &&
                  Object.entries(selectedAgent.invoke_agent_input.function.parameters.properties).map(([key, value]) => (
                    <div key={key} className="form-control">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {value.description || key}
                      </label>
                      <input
                        type="text"
                        value={agentInputs[key] || ""}
                        onChange={(e) => handleInputChange(key, e.target.value)}
                        className="w-full p-2 border rounded-md outline-none focus:ring-2 focus:ring-primary"
                      />
                    </div>
                  ))
                }
              </div>

              <div className="flex gap-2">
                {!isSaved && (
                  <button
                    onClick={handleSaveAgent}
                    disabled={isSaving}
                    className="flex-1 py-2 text-center bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 disabled:opacity-50 flex justify-center items-center gap-1"
                  >
                    <Bookmark className="w-4 h-4" />
                    <span>{isSaving ? "Saving..." : "Save Agent"}</span>
                  </button>
                )}
                <button
                  onClick={handleRunAgent}
                  disabled={isRunning}
                  className={`py-2 text-center bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50 ${isSaved ? 'w-full' : 'flex-1'}`}
                >
                  {isRunning ? (
                    <span className="flex items-center justify-center">
                      Agent is heavylifting. {LIFTING_EMOJIS[liftingIndex]}
                    </span>
                  ) : "Run Agent"}
                </button>
              </div>
            </div>
          )
        ) : showSearchResults ? (
          <div className="mt-4">
            <button
              onClick={handleBackToSearch}
              className="mb-4 text-sm px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300"
            >
              ← Back to search
            </button>

            <div className="max-h-[60vh] overflow-auto">
              {agents.length > 0 ? (
                <div className="space-y-3">
                  {agents.map((agent) => (
                    <div
                      key={agent.agent_id}
                      onClick={() => handleAgentSelect(agent, true)}
                      className="p-3 border rounded-md hover:bg-gray-50 cursor-pointer flex items-center"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-3">
                        <img
                          src={agent.icon?.startsWith("/") ? `https://agent.ai${agent.icon}` : agent.icon || "/icons/ai-agent.svg"}
                          alt={agent.name}
                          className="w-5 h-5"
                        />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{agent.name}</h4>
                        <p className="text-xs text-gray-500 line-clamp-2">{agent.description}</p>
                      </div>
                      <div className="flex gap-2">
                        {savedAgentIds.includes(agent.agent_id) ? (
                          <button
                            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <BookmarkCheck className="w-3 h-3" />
                            <span>Saved</span>
                          </button>
                        ) : (
                          <button
                            className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center gap-1"
                            onClick={(e) => handleSaveAgent(e, agent)}
                          >
                            <Bookmark className="w-3 h-3" />
                            <span>Save</span>
                          </button>
                        )}
                        <button className="px-3 py-1 text-sm bg-primary text-white rounded-md hover:bg-primary/90">
                          Run
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-6">
                  <p className="text-gray-500">No agents found matching your search.</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSearch} className="space-y-4 mt-4">
            <div className="form-control">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Query
              </label>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g., document analysis, content writing"
                className="w-full p-2 border rounded-md outline-none focus:ring-2 focus:ring-primary"
              />

              {/* Common query suggestions */}
              <div className="mt-2 flex flex-wrap gap-2">
                {COMMON_QUERIES.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="px-3 py-1 bg-gray-100 rounded-full text-sm font-medium text-gray-500">OR</div>
            </div>

            <div className="form-control">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Use Case
              </label>
              <textarea
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                placeholder="Describe what you want to accomplish, e.g., 'I need to analyze financial statements and create summary reports'"
                className="w-full p-2 border rounded-md outline-none focus:ring-2 focus:ring-primary h-24 resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading || (!query && !intent)}
              className="w-full py-2 text-center bg-primary text-white rounded-md hover:bg-primary/90 disabled:opacity-50"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  Searching web for your agent. {createEarthString(earthCount)}
                </span>
              ) : "Search Agents"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SearchAgentsDialog; 